import { z } from 'zod';
import { TFunction } from 'i18next';
import { parsePhoneNumber } from 'awesome-phonenumber';
import validator from 'validator';

import { isIbanBlocked, parseCreditCard } from '.';

const REQUIRED = 'validation_required';

export const UserAddressSchema = (t: TFunction) => {
  return z.object({
    state: DropdownOptionSchema(t),
    street1: z.string().trim().min(1, t(REQUIRED)),
    street2: z.string().trim(),
    city: z.string().trim().min(1, t(REQUIRED)),
    zipCode: z.string().trim().min(1, t(REQUIRED)),
    country: DropdownOptionSchema(t),
  }).refine((data) => {
    if (!data.country.value) {
      return false;
    }
    if (['US', 'BR'].includes(data.country.value) && !data.state.value) {
      return false;
    }
    return true;
  }, {
    message: t(REQUIRED),
    path: ['state'],
  });
};

export const PhoneNumberSchema = (t: TFunction) => {
  return z.string().superRefine((value, ctx) => {
    if (!value || value.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t(REQUIRED),
        path: [t('emer_field1_option3')],
      });
      return;
    }

    const phone = parsePhoneNumber(value);
    if (!phone.valid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('validation_phone_number'),
        path: [t('emer_field1_option3')],
      });
    }
  });
};

export const UserInfoSchema = (t: TFunction) => {
  return z.object({
    acceptTerms: z.boolean().refine((value) => value === true, {
      message: t(REQUIRED),
    }),
    agencyName: z.string().trim().min(1, t(REQUIRED)),
    agencyAddress: UserAddressSchema(t),
    emailAddress: z.string().trim().min(1, t(REQUIRED)).email(t('error_msg_email')),
    familyName: z.string().trim().min(1, t(REQUIRED)),
    givenName: z.string().trim().min(1, t(REQUIRED)),
    phoneNumber: PhoneNumberSchema(t),
    title: z.string().trim().min(1, t(REQUIRED)),
  });
};

export const DropdownOptionSchema = (t: TFunction) => {
  return z.object({
    label: z.string(),
    value: z.string(),
  }, { message: t(REQUIRED) });
};

export const IdentifierPayloadSchema = (t: TFunction) => {
  return z
    .object({
      mopType: DropdownOptionSchema(t),
      mop: z.string().optional(),
      routingNumber: z.string().optional(),
      accountNumber: z.string().optional(),
      branchNumber: z.string().optional(),
      digit: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const numericRegex = /^[0-9]+$/;

      switch (data.mopType.value) {
        case 'CC':
          if (!parseCreditCard(data.mop || '').isValid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation_credit_card_invalid'),
              path: ['mop'],
            });
          }
          break;

        case 'EU_DIRECT_DEBIT':
          if (!validator.isIBAN(data.mop || '')) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation_iban'),
              path: ['mop'],
            });
          }

          if (isIbanBlocked(data.mop)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation_iban_netflix'),
              path: ['mop'],
            });
          }
          break;

        case 'PAYPAL':
          if (!z.string().email().safeParse(data.mop).success) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation_paypal'),
              path: ['mop'],
            });
          }
          break;

        case 'US_BA':
          if (!data.routingNumber || data.routingNumber.trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(REQUIRED),
              path: ['routingNumber'],
            });
          } else if (!numericRegex.test(data.routingNumber!) || data.routingNumber!.length !== 9) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation_routing_number'),
              path: ['routingNumber'],
            });
          }

          if (!data.accountNumber || data.accountNumber.trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(REQUIRED),
              path: ['accountNumber'],
            });
          } else if (!numericRegex.test(data.accountNumber!) || data.accountNumber!.length < 6 || data.accountNumber!.length > 17) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('field_account_number_validation'),
              path: ['accountNumber'],
            });
          }
          break;

        case 'BR_BA':
          if (!data.accountNumber || data.accountNumber.trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(REQUIRED),
              path: ['accountNumber'],
            });
          } else if (data.accountNumber!.length > 11) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('mop_type7_field2_error'),
              path: ['accountNumber'],
            });
          }

          if (!data.branchNumber || data.branchNumber.trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(REQUIRED),
              path: ['branchNumber'],
            });
          } else if (data.branchNumber!.length > 4) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('mop_type8_field1_error'),
              path: ['branchNumber'],
            });
          }

          if (!data.digit || data.digit.trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(REQUIRED),
              path: ['digit'],
            });
          } else if (data.digit!.length > 2) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('mop_type8_field2_error'),
              path: ['digit'],
            });
          }
          break;
      };
    });
};

export const EmailSchema = (t: TFunction) => {
  return z.string().superRefine((value, ctx) => {
    if (!value || value.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t(REQUIRED),
        path: [t('field_email_address')],
      });
      return;
    }

    if (!validator.isEmail(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('error_msg_email'),
        path: [t('field_email_address')],
      });
    }
  });
};

export const MemberIdPayloadSchema = (t: TFunction) => {
  return z.object({
    emailAddresses: z.array(EmailSchema(t)),
    phoneNumbers: z.array(PhoneNumberSchema(t)),
    identifierPayloads: z.array(IdentifierPayloadSchema(t)),
    atLeastOne: z.boolean().optional(),
    other: z.string().trim(),
  }).refine((data) => {
    return data.emailAddresses.length > 0 ||
      data.phoneNumbers.length > 0 ||
      data.identifierPayloads.length > 0 ||
      data.other.length > 0;
  }, {
    message: t('validation_desc'),
    path: ['atLeastOne'],
  });
};

export const EmergencyFormSchema = (t: TFunction) => {
  return z.object({
    memberIdPayload: MemberIdPayloadSchema(t),
    eventDate: z.string().refine((date) => new Date(date) <= new Date(), { message: t('validation_end_date_2') }),
    description1: z.string().trim().min(1, t(REQUIRED)),
    description2: z.string().trim().min(1, t(REQUIRED)),
    description3: z.string().trim().min(1, t(REQUIRED)),
    description4: z.string().trim().min(1, t(REQUIRED)),
    signatureAgreement: z.boolean().refine((value) => value === true, {
      message: t(REQUIRED),
    }),
    signatureToMatch: z.string(),
    signature: z.string().trim().min(1, t(REQUIRED)),
    consentDate: z.string().refine((date) => new Date(date) <= new Date(), { message: t('validation_end_date_2') }),
  }).refine((data) => {
    const signatureToMatch = data.signatureToMatch.toLowerCase();
    return signatureToMatch && signatureToMatch.trim() === data.signature.toLowerCase().trim();
  }, {
    message: t("validation_name_match"),
    path: ['signature'],
  });
};
